import React, { useState } from 'react';
import { Bot, Loader2, Send, ShieldAlert, Sparkles, X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const createMessageId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const INITIAL_MESSAGES = [
  {
    id: createMessageId(),
    role: 'assistant',
    text: 'Olá. Sou o Assistente IA do Admin. Você já está autenticado e tem acesso padrão para consultar dados e solicitar operações.'
  }
];

export default function AdminAIAssistantWidget({ auth }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [loading, setLoading] = useState(false);
  const [pendingExecution, setPendingExecution] = useState(null);

  const isAuthenticated = Boolean(auth?.isAuthenticated && auth?.session?.user?.id);
  const isAdmin = Boolean(auth?.isAdmin);
  const canRender = isAdmin;
  const canSubmit = Boolean(prompt.trim() && !loading);

  if (!canRender) return null;

  const appendMessage = (role, text, metadata = null) => {
    setMessages((prev) => [
      ...prev,
      { id: createMessageId(), role, text, metadata }
    ]);
  };

  const getToken = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const messageText = prompt.trim();
    appendMessage('user', messageText);
    setPrompt('');
    setLoading(true);

    try {
      const token = await getToken();
      if (!token) {
        appendMessage('assistant', 'Erro: sessão expirada. Faça login novamente para usar o assistente.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-ai-assistant', {
        headers: { Authorization: `Bearer ${token}` },
        body: {
          prompt: messageText,
          userContext: {
            authenticated: isAuthenticated,
            userId: auth?.session?.user?.id || null,
            email: auth?.session?.user?.email || null,
            role: auth?.profileRole || null
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Falha ao processar solicitação');
      }

      const assistantText = data?.message || 'Não consegui processar sua solicitação agora.';
      appendMessage('assistant', assistantText, {
        decision: data?.decision || null,
        requiresDoubleValidation: Boolean(data?.requiresDoubleValidation),
        authenticatedMode: data?.authenticatedMode || null
      });
      if (data?.requiresDoubleValidation && data?.executionToken) {
        setPendingExecution({
          executionToken: data.executionToken,
          summary: data.summary || assistantText
        });
      } else {
        setPendingExecution(null);
      }
    } catch (err) {
      let serverMessage = '';
      try {
        if (err?.context) {
          const payload = await err.context.json();
          if (payload?.message) serverMessage = String(payload.message);
          if (!serverMessage && payload?.error) serverMessage = String(payload.error);
        }
      } catch {}
      appendMessage('assistant', `Erro: ${serverMessage || err.message || 'Não foi possível concluir.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExecutePending = async () => {
    if (!pendingExecution || loading) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        appendMessage('assistant', 'Erro: sessão expirada. Faça login novamente para usar o assistente.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-ai-assistant', {
        headers: { Authorization: `Bearer ${token}` },
        body: {
          prompt: pendingExecution.summary || 'Executar ação pendente',
          action: 'execute',
          executionToken: pendingExecution.executionToken,
          confirmExecution: true
        }
      });

      if (error) {
        throw new Error(error.message || 'Falha ao executar ação');
      }

      const assistantText = data?.message || 'Ação executada.';
      appendMessage('assistant', assistantText, {
        decision: data?.decision || null,
        execution: data?.execution || null
      });
      if (data?.requiresDoubleValidation && data?.executionToken) {
        setPendingExecution({
          executionToken: data.executionToken,
          summary: data.summary || assistantText
        });
      } else {
        setPendingExecution(null);
      }
    } catch (err) {
      appendMessage('assistant', `Erro: ${err.message || 'Não foi possível executar.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[180] h-14 w-14 rounded-full bg-orange-600 text-white shadow-xl hover:bg-orange-500 transition flex items-center justify-center"
      >
        {isOpen ? <X size={24} /> : <Bot size={24} />}
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[180] w-[92vw] max-w-[420px] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-orange-500" />
              <span className="text-sm font-bold text-white">Assistente IA Admin</span>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {isAuthenticated ? 'Autenticado' : 'Não autenticado'}
            </span>
          </div>

          <div className="h-[340px] overflow-y-auto p-3 space-y-3 bg-slate-900">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`rounded-xl px-3 py-2 text-sm border ${
                  msg.role === 'assistant'
                    ? 'bg-slate-800 border-slate-700 text-slate-100'
                    : 'bg-orange-600/20 border-orange-500/40 text-orange-100'
                }`}
              >
                <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1 font-bold">
                  {msg.role === 'assistant' ? 'Assistente' : 'Você'}
                </div>
                <div className="whitespace-pre-wrap">{msg.text}</div>
                {msg.metadata?.requiresDoubleValidation && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-300 font-bold uppercase tracking-widest">
                    <ShieldAlert size={12} /> Validação necessária
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="rounded-xl px-3 py-2 text-sm border bg-slate-800 border-slate-700 text-slate-100 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Processando...
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-3 border-t border-slate-800 bg-slate-950 space-y-2">
            {pendingExecution && (
              <div className="p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 space-y-2">
                <div className="text-[11px] text-amber-200 font-bold uppercase tracking-wider flex items-center gap-1">
                  <ShieldAlert size={12} /> Confirmação de alteração
                </div>
                <div className="text-xs text-slate-200">{pendingExecution.summary}</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingExecution(null);
                      appendMessage('assistant', 'Alteração cancelada pelo Admin.');
                    }}
                    className="flex-1 text-xs py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition"
                    disabled={loading}
                  >
                    Não
                  </button>
                  <button
                    type="button"
                    onClick={handleExecutePending}
                    className="flex-1 text-xs py-2 rounded-lg bg-orange-600 text-white font-bold disabled:bg-slate-700 disabled:text-slate-500 transition"
                    disabled={loading}
                  >
                    Sim, executar
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Pergunte ou solicite uma ação..."
                className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
              />
              <button
                type="submit"
                disabled={!canSubmit}
                className="h-10 w-10 rounded-lg bg-orange-600 disabled:bg-slate-700 disabled:text-slate-500 text-white flex items-center justify-center transition"
              >
                <Send size={16} />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
